// src/pages/HomePage.jsx — SEPARATE BOXES LAYOUT (FINAL)
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import useStore from '../store/useStore';
import toast from 'react-hot-toast';
// 🐌 PERFORMANCE: Whole-library import - [Severity: 🟠 HIGH - 55%]
// CURRENT: `import { throttle } from 'lodash'` pulls in the full lodash package (unless the build
// pipeline has babel-plugin-lodash or a similar tree-shaking transform configured, which is not
// present in this CRA setup) just to use one ~15-line utility function, on the app's most-visited
// page (HomePage, statically bundled per the App.js finding).
// IMPACT: Adds tens of KB of unused code to the initial JS bundle every visitor downloads and parses,
// directly hurting first load time on the landing page.
// FIX: Import the scoped module instead - `import throttle from 'lodash/throttle'` (or better,
// `lodash-es` with tree-shaking, or a 10-line hand-rolled throttle) - to only ship the function
// actually used.
import throttle from 'lodash/throttle';
import Logo from '../components/Logo';
import SEO from '../components/SEO';
import apiClient from '../api/axiosConfig';
import { GLOBAL_STYLES } from '../components/gameroom/styles';
import {
  TrophyIcon,
  SwordIcon,
  GiftIcon,
  CheckCircleIcon,
  ClockIcon,
  StarIcon,
  ExclamationIcon,
  CrossIcon,
  SignOutIcon,
  PlayIcon,
  UsersIcon,
  KeyIcon,
} from '../components/gameroom/SvgIcons';

const avatarUrls = Array.from({ length: 28 }, (_, i) =>
  `https://cdn.jsdelivr.net/gh/HamzaRSP/avatars@main/avatar${i + 1}.png`
);

/* ─── DESIGN TOKENS ────────────────────────────────────────────── */
const D = {
  page: {
    background: '#f8f9fa',
    minHeight: '100vh',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: '#1a1a2e',
    display: 'flex',
    flexDirection: 'column',
  },

  topbar: {
    background: '#fff',
    borderBottom: '1px solid #e9ecef',
    padding: '0 24px',
    height: '52px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },

  playerCount: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '5px 14px',
    border: '1px solid #e9ecef',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '500',
    color: '#495057',
    background: '#fff',
  },

  logoutTopBtn: {
    padding: '5px 10px',
    background: '#fff',
    border: '1px solid #e9ecef',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '11px',
    color: '#495057',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    fontFamily: 'inherit',
    transition: 'all 0.15s ease',
  },

  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '32px 24px',
    gap: '16px',
  },

  container: {
    width: '100%',
    maxWidth: '440px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },

  box: {
    background: '#fff',
    border: '1px solid #e9ecef',
    borderRadius: '8px',
    padding: '20px',
  },

  // ─── AVATAR BOX ───
  avatarBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    padding: '28px 20px',
  },

  avatarWrapper: {
    position: 'relative',
  },

  avatarRing: (progress) => ({
    width: '88px',
    height: '88px',
    borderRadius: '50%',
    padding: '3px',
    background: `conic-gradient(#237032 ${progress * 3.6}deg, #e9ecef ${progress * 3.6}deg)`,
  }),

  avatarImg: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '3px solid #fff',
    background: '#fff',
  },

  editBtn: {
    position: 'absolute',
    bottom: '-2px',
    right: '-2px',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: '#1864ab',
    color: '#fff',
    border: '3px solid #fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
  },

  avatarText: {
    textAlign: 'center',
    fontSize: '11px',
    color: '#495057',
  },

  // ─── USERNAME BOX ───
  username: {
    fontSize: '17px',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 2px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },

  levelBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '3px 10px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '600',
    background: 'rgba(64,192,87,0.1)',
    color: '#237032',
    border: '1px solid rgba(64,192,87,0.2)',
  },

  onlineInline: {
    fontSize: '11px',
    color: '#495057',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    whiteSpace: 'nowrap',
  },

  // ─── PROGRESS BOX ───
  progressBox: {
    padding: '16px 20px',
  },

  progressLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '6px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#495057',
  },

  progressTrack: {
    height: '6px',
    background: '#f1f3f5',
    borderRadius: '3px',
    overflow: 'hidden',
  },

  progressFill: (pct) => ({
    height: '100%',
    width: `${Math.min(pct, 100)}%`,
    background: '#237032',
    borderRadius: '3px',
    transition: 'width 0.3s ease',
  }),

  progressMarks: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '4px',
    fontSize: '10px',
    color: '#495057',
  },

  // ─── PLAY BOX ───
  playBox: {
    padding: '20px',
  },

  playBtn: {
    width: '100%',
    padding: '16px 32px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    fontFamily: 'inherit',
    background: '#237032',
    color: '#fff',
    boxShadow: '0 2px 8px rgba(64,192,87,0.2)',
    transition: 'all 0.15s ease',
  },

  playBtnDisabled: {
    width: '100%',
    padding: '16px 32px',
    border: '1px solid #e9ecef',
    borderRadius: '8px',
    cursor: 'not-allowed',
    fontSize: '16px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    fontFamily: 'inherit',
    background: '#f8f9fa',
    color: '#495057',
  },

  // ─── PRIZE BOX ───
  prizeBox: {
    padding: '16px 20px',
    background: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: '8px',
  },

  prizeClaimedBox: {
    padding: '16px 20px',
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '8px',
  },

  prizeTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#b45309',
    marginBottom: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },

  prizeText: {
    fontSize: '12px',
    color: '#78350f',
    margin: '0 0 12px',
  },

  prizeClaimedTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#237032',
    marginBottom: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },

  prizeClaimedText: {
    fontSize: '12px',
    color: '#065f46',
    margin: 0,
  },

  claimBtn: {
    padding: '10px 20px',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontFamily: 'inherit',
    background: '#b54708',
    color: '#fff',
  },

  // ─── BEFORE LOGIN ───
  heroTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 4px',
    textAlign: 'center',
  },

  heroSubtitle: {
    fontSize: '12px',
    color: '#495057',
    margin: '0 0 20px',
    textAlign: 'center',
  },

  tabsBox: {
    padding: '4px',
    background: '#f1f3f5',
    borderRadius: '8px',
    display: 'flex',
    gap: '4px',
    marginBottom: '20px',
  },

  tab: (active) => ({
    flex: 1,
    padding: '10px 16px',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    fontFamily: 'inherit',
    background: active ? '#fff' : 'transparent',
    color: active ? '#1a1a2e' : '#495057',
    boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
    transition: 'all 0.15s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  }),

  // Key Entry
  keyLabel: {
    display: 'block',
    fontSize: '10px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: '#495057',
    marginBottom: '6px',
    textAlign: 'center',
  },

  keyInput: {
    width: '100%',
    padding: '14px 16px',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    fontSize: '17px',
    fontFamily: "'SF Mono', 'Fira Code', monospace",
    color: '#1a1a2e',
    background: '#fff',
    outline: 'none',
    boxSizing: 'border-box',
    textAlign: 'center',
    letterSpacing: '4px',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  },

  keyInputFocus: {
    borderColor: '#339af0',
    boxShadow: '0 0 0 3px rgba(51,154,240,0.1)',
  },

  startBtn: (hasKey) => ({
    width: '100%',
    padding: '14px 24px',
    border: 'none',
    borderRadius: '8px',
    cursor: hasKey ? 'pointer' : 'default',
    fontSize: '15px',
    fontWeight: '700',
    fontFamily: 'inherit',
    background: hasKey ? '#237032' : '#e9ecef',
    color: hasKey ? '#fff' : '#495057',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    boxShadow: hasKey ? '0 2px 8px rgba(64,192,87,0.25)' : 'none',
    transition: 'all 0.2s ease',
    marginBottom: '12px',
  }),

  getKeyLink: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    color: '#495057',
    fontFamily: 'inherit',
    padding: '4px 8px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },

  getKeyHighlight: {
    color: '#1864ab',
    fontWeight: '600',
  },

  // Input (legacy - keep for compatibility)
  input: {
    width: '100%',
    padding: '12px 16px',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    fontSize: '15px',
    fontFamily: 'inherit',
    color: '#1a1a2e',
    background: '#fff',
    outline: 'none',
    boxSizing: 'border-box',
    textAlign: 'center',
    letterSpacing: '2px',
  },

  inputFocus: {
    borderColor: '#339af0',
    boxShadow: '0 0 0 3px rgba(51,154,240,0.1)',
  },

  btn: {
    width: '100%',
    padding: '11px 20px',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontFamily: 'inherit',
  },

  btnPrimary: { background: '#1864ab', color: '#fff' },
  btnWarning: { background: '#b54708', color: '#fff' },
  btnOutline: { background: '#fff', color: '#495057', border: '1px solid #dee2e6' },

  errorBox: {
    padding: '10px 14px',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    fontSize: '12px',
    color: '#dc2626',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '12px',
  },

  // Steps
  stepsTitle: {
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#495057',
    marginBottom: '10px',
  },

  stepsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },

  stepItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '12px',
    color: '#495057',
  },

  stepNum: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    background: '#f1f3f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
    fontWeight: '700',
    color: '#495057',
    flexShrink: 0,
  },

  // Modal
  modal: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '16px',
  },
  modalCard: {
    background: '#fff', border: '1px solid #e9ecef', borderRadius: '8px',
    maxWidth: '420px', width: '100%', maxHeight: '90vh', overflow: 'auto',
  },
  modalHeader: {
    padding: '16px 20px', borderBottom: '1px solid #f1f3f5',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    fontSize: '14px', fontWeight: '600', color: '#1a1a2e',
  },
  modalBody: { padding: '20px', maxHeight: '350px', overflow: 'auto' },
  modalFooter: {
    padding: '14px 20px', borderTop: '1px solid #f1f3f5',
    display: 'flex', gap: '8px', background: '#fafbfc',
  },

  spinner: {
    width: '14px', height: '14px',
    border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff',
    borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block',
  },

  footer: {
    padding: '16px', textAlign: 'center', fontSize: '10px',
    color: '#495057', borderTop: '1px solid #e9ecef', background: '#fff',
  },

  countdown: {
    textAlign: 'center', fontSize: '12px', color: '#495057',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
  },
};

/* ─── LOADING DOTS (used while the online-player count hasn't arrived yet) ── */
const LoadingDots = () => (
  <span style={{ display: 'inline-flex', gap: '3px', alignItems: 'center' }}>
    {[0, 1, 2].map((i) => (
      <span key={i} style={{
        width: '4px', height: '4px', borderRadius: '50%', background: '#adb5bd',
        display: 'inline-block', animation: 'dotPulse 1.2s ease-in-out infinite',
        animationDelay: `${i * 0.15}s`,
      }} />
    ))}
  </span>
);

/* ─── MAIN COMPONENT ─────────────────────────────────────────────── */
function HomePage() {
  const [localAccessKey, setLocalAccessKey] = useState('');
  const [error, setError] = useState('');
  const [showCarousel, setShowCarousel] = useState(false);
  const [logoutCountdown, setLogoutCountdown] = useState(null);
  const [isClaiming, setIsClaiming] = useState(false);
  const [onlinePlayers, setOnlinePlayers] = useState(
    Number(localStorage.getItem('lastPlayerCount')) || null
  );
  const [verifyRequested, setVerifyRequested] = useState(false);
  const [focusedField, setFocusedField] = useState(false);
  const [activeTab, setActiveTab] = useState('play');

  // Selecting individual fields (instead of calling useStore() with no selector) means this page -
  // the app's most-visited route - only re-renders when one of these specific fields changes, not
  // on every write to the store from anywhere else in the app (same fix already applied to
  // GameRoom.jsx).
  const accessKey         = useStore((s) => s.accessKey);
  const setAccessKey      = useStore((s) => s.setAccessKey);
  const userData          = useStore((s) => s.userData);
  const setUserData       = useStore((s) => s.setUserData);
  const avatar            = useStore((s) => s.avatar);
  const setAvatar         = useStore((s) => s.setAvatar);
  const tempAvatar        = useStore((s) => s.tempAvatar);
  const setTempAvatar     = useStore((s) => s.setTempAvatar);
  const isHydrated        = useStore((s) => s.isHydrated);
  const loadingUserData   = useStore((s) => s.loadingUserData);
  const setLoadingUserData = useStore((s) => s.setLoadingUserData);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const socket = useStore.getState().getSocket();

  // SECURITY NOTE: This implementation appears secure against the unvalidated-referral-code finding
  // formerly noted here - the real risk (a NoSQL-injection-style object via bracket query notation, e.g.
  // ?ref[$ne]=null, since ref eventually reaches ReferralUser.findOne({ referralCode: ref })) is already
  // blocked globally by safeSanitize/sanitizeAndValidate (server.js), which cover both req.query and
  // req.body and strip/reject `$`-prefixed keys before any route handler runs. A plain junk/oversized
  // string just fails the equality lookup harmlessly (no referrer found, no credit given). As cheap
  // defense-in-depth, `ref` is now also validated against a strict allowlist pattern before being
  // persisted to localStorage at all.
  // ⚡ SEVERITY: ⚪ INFO - 0%
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref && /^[A-Za-z0-9_-]{1,32}$/.test(ref)) {
      localStorage.setItem('referral_code', ref);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const locationState = location.state || {};
    if (locationState.banned) {
      toast.error(`BANNED: ${locationState.reason || 'Cheating'}`, { duration: 10000 });
      useStore.getState().clearAll(); setAccessKey(''); setUserData(null); setLocalAccessKey('');
    }
    if (locationState.opponentCheated) {
      toast.success(`Opponent cheated! You win.`, { duration: 5000 });
    }
  }, [location.state]);

  // Uses the single shared socket (useStore.getState().getSocket()) for the public active-player
  // count too, instead of opening a second, separate socket.io-client connection just for that -
  // the server broadcasts `activePlayerCount` via io.emit (to every connected socket, authenticated
  // or not) and `getActivePlayerCount` has no auth requirement either, so one connection is enough.
  useEffect(() => {
    const sock = useStore.getState().getSocket();
    if (!sock) return;
    const requestCount = () => sock.emit('getActivePlayerCount');
    if (sock.connected) requestCount();
    else sock.once('connect', requestCount);

    const h = {
      activePlayerCount: ({ count }) => { localStorage.setItem('lastPlayerCount', count); setOnlinePlayers(count); },
      onlinePlayers: (c) => setOnlinePlayers(typeof c === 'number' ? c : c?.count ?? 0),
      authFailed: ({ message }) => {
        toast.error(message); useStore.getState().clearAll(); setAccessKey(''); setUserData(null); setLocalAccessKey('');
      },
      eliminated: ({ message }) => {
        toast.error(message); useStore.getState().clearAll(); setAccessKey(''); setUserData(null); setLocalAccessKey(''); setLogoutCountdown(null);
      },
    };
    Object.entries(h).forEach(([ev, fn]) => { sock.off(ev); sock.on(ev, fn); });
    return () => {
      sock.off('connect', requestCount);
      Object.entries(h).forEach(([ev, fn]) => sock.off(ev, fn));
    };
  }, []);

  useEffect(() => {
    if (!socket) return;
    const send = throttle(() => { if (useStore.getState().accessKey && socket?.connected) socket.emit('userActivity'); }, 10000);
    ['mousemove','mousedown','keydown','touchstart','click'].forEach(e => window.addEventListener(e, send, { passive: true }));
    return () => ['mousemove','mousedown','keydown','touchstart','click'].forEach(e => window.removeEventListener(e, send));
  }, [socket]);

  useEffect(() => {
    if (!isHydrated || !accessKey || !verifyRequested) return;
    let cancelled = false;
    const ctrl = new AbortController();
    (async () => {
      try {
        setLoadingUserData(true);
        const { data } = await apiClient.post('/keys/verify', { key: accessKey }, { signal: ctrl.signal });
        if (cancelled) return;
        setUserData(data);
        const av = data.avatar || 'https://cdn.jsdelivr.net/gh/HamzaRSP/avatars@main/avatar12.png';
        setAvatar(av); setTempAvatar(av);
        // A winner who already claimed their prize is intentionally rejected by the socket
        // 'authenticate' handler (server.js) - they're done playing, no real-time connection needed.
        // Skip that call entirely and send them straight to withdraw instead of letting the
        // guaranteed authFailed below wipe this session we just successfully verified.
        if (data.prizeClaimed) {
          navigate(`/withdraw?key=${accessKey}`);
          return;
        }
        await useStore.getState().authenticateSocket(accessKey);
      // SECURITY NOTE: This implementation appears secure against XSS via toast messages — err.response?.data?.message
      // is passed to react-hot-toast's toast.error(), which renders as plain text (not dangerouslySetInnerHTML),
      // so even if the backend ever echoed attacker-supplied content in an error message, it would not execute as HTML.
      // ⚡ SEVERITY: ⚪ INFO - 0%
      } catch (err) {
        if (cancelled || err.name === 'AbortError') return;
        toast.error(err.response?.data?.message || err.message  || 'Auth failed');
        useStore.getState().clearAll(); setAccessKey(''); setUserData(null); setLocalAccessKey('');
      } finally { if (!cancelled) setLoadingUserData(false); }
    })();
    return () => { cancelled = true; ctrl.abort(); };
  }, [verifyRequested, accessKey, isHydrated]);

  useEffect(() => { if (isHydrated && accessKey && !verifyRequested) setVerifyRequested(true); }, [isHydrated, accessKey]);

  const handleVerify = () => {
    if (!localAccessKey.trim()) { setError('Please enter an access key'); return; }
    setError(''); setAccessKey(localAccessKey); setVerifyRequested(true);
  };

  const handleLogout = useCallback(() => {
    const sock = useStore.getState().getSocket();
    if (sock?.connected) { sock.emit('logout'); sock.emit('getActivePlayerCount'); }
    useStore.getState().cleanupSocket(); useStore.getState().clearAll();
    setLocalAccessKey(''); setLogoutCountdown(null);
    toast.success('Logged out');
  }, []);

  // SECURITY NOTE: This implementation appears secure against the key-as-bearer-credential finding
  // formerly noted here - `key` is no longer sent in the body; PATCH /keys/avatar now authenticates via
  // the httpOnly playerToken cookie (set once by the /keys/verify call above) instead of a resent raw
  // key, which apiClient already carries automatically (withCredentials:true, see axiosConfig.jsx).
  // ⚡ SEVERITY: ⚪ INFO - 0%
  const handleConfirmAvatar = async () => {
    try {
      await apiClient.patch('/keys/avatar', { avatar: tempAvatar });
      setAvatar(tempAvatar); setShowCarousel(false); toast.success('Avatar updated!');
    } catch { toast.error('Error saving avatar.'); }
  };

  const handleJoinGame = () => {
    if (userData?.progress >= 100) { toast.error('Game completed. Cannot play again.'); return; }
    navigate('/game');
  };

  const handleClaimPrize = useCallback(async () => {
    setIsClaiming(true);
    try {
      const key = useStore.getState().accessKey;
      if (!key) { toast.error('No access key'); return; }
      // SECURITY NOTE: This implementation appears secure against the key-as-bearer-credential finding
      // formerly noted here - POST /keys/claim-prize now authenticates via the httpOnly playerToken
      // cookie (requirePlayerAuth) instead of a raw key resent in the body. `key` is still read from the
      // store here only to build the /withdraw?key=... redirect URL below (Withdraw.jsx's own
      // known/accepted moment-of-navigation URL exposure, unchanged by this fix - see its own note),
      // not sent as a credential in this request. The follow-up /keys/verify call also drops the body
      // key entirely, relying on the same cookie (refresh mode, see keyRoutes.js).
      // ⚡ SEVERITY: ⚪ INFO - 0%
      const res = await apiClient.post('/keys/claim-prize');
      if (res.data.success) {
        try { const v = await apiClient.post('/keys/verify'); setUserData(v.data); } catch {}
        navigate(res.data.redirect || `/withdraw?key=${key}`);
      } else toast.error(res.data.message || 'Claim failed');
    } catch (err) {
      const m = err.response?.data?.message || '';
      if (m.includes('already') || m.includes('claimed')) navigate(`/withdraw?key=${useStore.getState().accessKey}`);
      else toast.error(m || 'Network error');
    } finally { setIsClaiming(false); }
  }, [setUserData, navigate]);

  useEffect(() => {
    if (logoutCountdown === null) return;
    if (logoutCountdown > 0) { const t = setTimeout(() => setLogoutCountdown(p => p - 1), 1000); return () => clearTimeout(t); }
    else if (logoutCountdown === 0) handleLogout();
  }, [logoutCountdown, handleLogout]);

  const isWinner = userData?.progress >= 100;
  const playerLabel = onlinePlayers == null ? <LoadingDots /> : onlinePlayers === 0 ? 'No players' : `${onlinePlayers} online`;

  if (!isHydrated || loadingUserData) {
    return (
      <div style={D.page}>
        <style>{GLOBAL_STYLES}</style>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes dotPulse{0%,80%,100%{opacity:.25} 40%{opacity:1}}`}</style>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh' }}>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'12px' }}>
            <div style={D.spinner} />
            <span style={{ color:'#495057', fontSize:'13px' }}>Verifying...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={D.page}>
      <SEO
        title="Jankenwin — Climb the Bracket, Compete for Real Prizes"
        description="Play Rock Paper Scissors online, climb the bracket, and compete for real cash prizes. Fast matchmaking, fair tournaments, payouts via cryptocurrency."
        path=""
      />
      <style>{GLOBAL_STYLES}</style>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Top Bar - only when logged in */}
      {userData && (
        <header style={D.topbar}>
          <div style={{ transform:'scale(0.6)', transformOrigin:'left center' }}><Logo /></div>
          <button onClick={handleLogout} style={D.logoutTopBtn}
            onMouseEnter={e => { e.currentTarget.style.color='#fa5252'; e.currentTarget.style.borderColor='#ffc9c9'; }}
            onMouseLeave={e => { e.currentTarget.style.color='#495057'; e.currentTarget.style.borderColor='#e9ecef'; }}>
            <SignOutIcon size={11} /> Logout
          </button>
        </header>
      )}

      <main style={D.main}>
        <div style={D.container}>

          {!userData ? (
            <>
              {/* HERO BOX */}
              <div style={{ ...D.box, padding: '28px 24px', textAlign: 'center' }}>
                <div style={{ transform:'scale(0.85)', transformOrigin:'center', marginBottom:'8px' }}><Logo /></div>
                <h1 style={D.heroTitle}>Climb the Bracket, Compete for Real Cash Prizes</h1>
                <p style={D.heroSubtitle}>Play Rock Paper Scissors tournaments and earn rewards</p>
                <span style={{
                  display:'inline-flex', alignItems:'center', gap:'6px',
                  padding:'5px 14px', border:'1px solid #e9ecef', borderRadius:'20px',
                  fontSize:'11px', fontWeight:'500', color:'#495057', background:'#fff',
                  marginBottom:'20px',
                }}>
                  
                  <span style={{ width:'6px', height:'6px', borderRadius:'50%', background: onlinePlayers > 0 ? '#237032' : '#495057', display:'inline-block' }} />
                  {playerLabel}
                </span>

                
                <div style={D.tabsBox}>
                  <button onClick={() => setActiveTab('play')} style={D.tab(activeTab === 'play')}>
                    <PlayIcon size={14} /> Play & Earn
                  </button>
                  <button onClick={() => { setActiveTab('invite'); navigate('/referral'); }} style={D.tab(activeTab === 'invite')}>
                    <UsersIcon size={14} /> Invite & Earn
                  </button>
                </div>

                {activeTab === 'play' && (
                  <>
                    <label style={D.keyLabel}>Access Key</label>
                    <div style={{ marginBottom: '12px' }}>
                      <input
                        style={{
                          ...D.keyInput,
                          ...(focusedField ? D.keyInputFocus : {}),
                        }}
                        type="text"
                        placeholder="Enter your key to start"
                        value={localAccessKey}
                        onChange={e => setLocalAccessKey(e.target.value)}
                        onKeyPress={e => e.key === 'Enter' && handleVerify()}
                        onFocus={() => setFocusedField(true)}
                        onBlur={() => setFocusedField(false)}
                      />
                    </div>

                    <button
                      onClick={handleVerify}
                      disabled={!localAccessKey}
                      style={D.startBtn(!!localAccessKey)}
                      onMouseEnter={e => { if(localAccessKey) e.currentTarget.style.transform='translateY(-1px)'; }}
                      onMouseLeave={e => { if(localAccessKey) e.currentTarget.style.transform='translateY(0)'; }}
                    >
                      <PlayIcon size={16} /> Start Playing
                    </button>

                    <button
                      onClick={() => navigate('/key-generator')}
                      style={D.getKeyLink}
                      onMouseEnter={e => { e.currentTarget.style.color = '#339af0'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = '#495057'; }}
                    >
                      <KeyIcon size={12} /> Need a key? <span style={D.getKeyHighlight}>Get Access Key</span>
                    </button>

                    {error && <div style={D.errorBox}><ExclamationIcon size={14} />{error}</div>}
                  </>
                )}
              </div>

              {/* STEPS BOX */}
              <div style={D.box}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={D.stepsTitle}>How It Works</div>
                  <Link
                    to="/faq"
                    style={{ background: '#fff', border: '1px solid #dee2e6', borderRadius: '20px', cursor: 'pointer', color: '#495057', fontSize: '11px', fontWeight: '700', fontFamily: 'inherit', padding: '4px 12px', textDecoration: 'none', display: 'inline-block' }}
                  >
                    FAQ
                  </Link>
                </div>
                <div style={D.stepsList}>
                  {[
                    'Get an access key from the key generator',
                    'Enter your key and choose your avatar',
                    'Play matches against other players',
                    'Win to progress; lose and you are eliminated',
                    'Reach 100% to claim your tournament prize'
                  ].map((s, i) => (
                    <div key={i} style={D.stepItem}><span style={D.stepNum}>{i+1}</span>{s}</div>
                  ))}
                </div>
              </div>

              {/* Referral Link Box */}
              <div onClick={() => navigate('/referral')} style={{
                ...D.box, padding: '14px 20px', display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', cursor: 'pointer', transition: 'all 0.15s ease',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#339af0'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e9ecef'; }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width:'36px', height:'36px', borderRadius:'8px', background:'#eff6ff', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <TrophyIcon size={18} color="#3b82f6" />
                  </div>
                  <div>
                    <div style={{ fontSize:'13px', fontWeight:'600', color:'#1a1a2e' }}>Referral Race</div>
                    <div style={{ fontSize:'11px', color:'#495057' }}>Invite friends and win cash prizes</div>
                  </div>
                </div>
                <span style={{ color:'#495057', fontSize:'18px' }}>→</span>
              </div>

              {/* Prediction Hub Link Box - deliberately styled/worded as a smaller side feature, not
                  alongside Play/Referral: predicting only earns a shot at a free tournament key, not
                  real money the way playing or referring does. */}
              <Link to="/predict/hub" style={{
                ...D.box, padding: '14px 20px', display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', cursor: 'pointer', transition: 'all 0.15s ease',
                textDecoration: 'none',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#495057'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e9ecef'; }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width:'36px', height:'36px', borderRadius:'8px', background:'#f8f9fa', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <KeyIcon size={18} color="#495057" />
                  </div>
                  <div>
                    <div style={{ fontSize:'13px', fontWeight:'600', color:'#1a1a2e' }}>Prediction Hub</div>
                    <div style={{ fontSize:'11px', color:'#495057' }}>Predict match winners for a chance at a free key</div>
                  </div>
                </div>
                <span style={{ color:'#495057', fontSize:'18px' }}>→</span>
              </Link>
            </>
          ) : (
            <>
              {/* AVATAR BOX */}
              <div style={{ ...D.box, ...D.avatarBox }}>
                <div style={D.avatarWrapper}>
                  <div style={D.avatarRing(userData.progress ?? 0)}>
                    <img src={avatar} alt="avatar" style={D.avatarImg} />
                  </div>
                  <button onClick={() => setShowCarousel(true)} style={D.editBtn}>✎</button>
                </div>
                <span style={D.avatarText}>Tap to change avatar</span>
              </div>

              {/* USERNAME BOX */}
              <div style={{ ...D.box, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h3 style={D.username}>
                    {userData.username}
                    <span style={D.levelBadge}><SwordIcon size={9} /> Lv.{userData.level}</span>
                  </h3>
                </div>
                <span style={D.onlineInline}>
                  <span style={{ width:'6px', height:'6px', borderRadius:'50%', background: onlinePlayers > 0 ? '#237032' : '#495057', display:'inline-block' }} />
                  {playerLabel}
                </span>
              </div>

              {/* PROGRESS BOX */}
              <div style={{ ...D.box, ...D.progressBox }}>
                <div style={D.progressLabel}>
                  <span>Tournament Progress</span>
                  <span style={{ fontWeight:'600', color:'#237032' }}>{userData.progress ?? 0}%</span>
                </div>
                <div style={D.progressTrack}>
                  <div style={D.progressFill(userData.progress ?? 0)} />
                </div>
                <div style={D.progressMarks}>
                  <span>Start</span><span>50%</span><span>Prize</span>
                </div>
              </div>

              {/* PRIZE BOX */}
              {isWinner && !userData.prizeClaimed && (
                <div style={D.prizeBox}>
                  <div style={D.prizeTitle}><TrophyIcon size={18} color="#b45309" /> Tournament Complete!</div>
                  <p style={D.prizeText}>{userData.prizeName || 'Mystery Prize'} is ready!</p>
                  <button onClick={handleClaimPrize} disabled={logoutCountdown > 0 || isClaiming} style={D.claimBtn}>
                    {isClaiming ? <><span style={D.spinner} /> Claiming...</> : <><GiftIcon size={16} /> Claim Prize</>}
                  </button>
                </div>
              )}

              {/* PRIZE CLAIMED BOX */}
              {userData.prizeClaimed && (
                <div style={D.prizeClaimedBox}>
                  <div style={D.prizeClaimedTitle}><CheckCircleIcon size={18} color="#237032" /> Prize Claimed!</div>
                  <p style={D.prizeClaimedText}>
                    {userData.prizeType && userData.prizeValue ? `${userData.prizeType}: ${userData.prizeValue}` : userData.prizeName || 'Mystery Prize'}
                  </p>
                </div>
              )}

              {/* PLAY BOX */}
              <div style={{ ...D.box, ...D.playBox }}>
                <button onClick={handleJoinGame} disabled={logoutCountdown > 0 || isWinner}
                  style={isWinner ? D.playBtnDisabled : D.playBtn}
                  onMouseEnter={e => { if(!isWinner) e.currentTarget.style.transform='translateY(-2px)'; }}
                  onMouseLeave={e => { if(!isWinner) e.currentTarget.style.transform='translateY(0)'; }}>
                  {isWinner ? <><CheckCircleIcon size={22} /> Tournament Completed</> : <><PlayIcon size={22} /> Play Now</>}
                </button>
                {logoutCountdown > 0 && (
                  <div style={{ ...D.countdown, marginTop:'12px' }}>
                    <ClockIcon size={12} /> Logging out in {logoutCountdown}s...
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>

      {/* Avatar Modal */}
      {showCarousel && (
        <div style={D.modal} onClick={() => setShowCarousel(false)}>
          <div style={D.modalCard} onClick={e => e.stopPropagation()}>
            <div style={D.modalHeader}><span>Choose Avatar</span>
              <button onClick={() => setShowCarousel(false)} style={{ background:'none', border:'none', cursor:'pointer', color:'#495057', padding:0 }}><CrossIcon size={16} /></button>
            </div>
            <div style={D.modalBody}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'10px' }}>
                {avatarUrls.map((url, idx) => (
                  <button key={idx} onClick={() => setTempAvatar(url)} style={{
                    aspectRatio:'1', border: tempAvatar === url ? '3px solid #339af0' : '2px solid #e9ecef',
                    borderRadius:'8px', padding:0, cursor:'pointer',
                    backgroundImage:`url(${url})`, backgroundSize:'cover', backgroundPosition:'center',
                    transform: tempAvatar === url ? 'scale(1.05)' : 'scale(1)', transition:'all 0.15s ease',
                  }} />
                ))}
              </div>
            </div>
            <div style={D.modalFooter}>
              <button onClick={handleConfirmAvatar} style={{ ...D.btn, ...D.btnPrimary, flex:1 }}>Confirm</button>
              <button onClick={() => setShowCarousel(false)} style={{ ...D.btn, ...D.btnOutline, flex:1 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div style={D.footer}>
        Jankenwin: One Key, One Run, One Champion
        <div style={{ marginTop: '6px' }}>
          <Link to="/terms" style={{ color: '#495057', marginRight: '12px' }}>Terms</Link>
          <Link to="/privacy" style={{ color: '#495057' }}>Privacy</Link>
        </div>
      </div>
    </div>
  );
}

export default HomePage;